import styled, { css, keyframes } from 'styled-components';

import { ANIM, COLORS, REDUCED_MOTION, SPACE, STORYBOARD_COMPOSE } from '@/nova/theme/tokens';

/** For a composer rail's scrolling setup while the selected version is locked (generated): every field is disabled,
 *  and native disabled controls swallow their own click, so clicks fall THROUGH them to the rail container (whose
 *  click handler, from useLockedNudge, then shakes the "New version from this" link, pointing at the one way to make
 *  an edit). Wheel and touch scroll still work: they act on the container, not the controls. Section headers stay
 *  enabled (a locked version can still be browsed) and the handler ignores them. Use it inside the rail's styled
 *  container: `${(p) => p.$locked && lockedRailPassThrough}`. */
export const lockedRailPassThrough = css`
  & :disabled {
    pointer-events: none;
  }
`;

/* A quick, subtle horizontal shake that draws the eye to the branch action when the owner clicks a locked (disabled)
   field: an edit is not possible here, a new version is. */
const nudgeShake = keyframes`
  0%, 100% { transform: translateX(0); }
  15% { transform: translateX(-4px); }
  30% { transform: translateX(4px); }
  45% { transform: translateX(-3px); }
  60% { transform: translateX(3px); }
  80% { transform: translateX(-1px); }
`;

/** The "+ New version from this" branch action, a text link (not a filled button) since it replaces Generate on a
 *  version that can no longer be edited in place. `$shake` (remounted via a changing key) plays the one-shot shake
 *  when a locked field is clicked. */
export const Link = styled.button<{ $shake?: boolean }>`
  display: inline-flex;
  align-items: center;
  /* 6px (off the scale): the video ad link's exact box, so lifting it here moved nothing on screen. */
  gap: 6px;
  flex: 0 0 auto;
  padding: 6px ${SPACE[4]};
  border: none;
  background: none;
  cursor: pointer;
  color: ${COLORS.accent};
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;

  &:hover:enabled {
    text-decoration: underline;
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  ${(p) =>
    p.$shake &&
    css`
      animation: ${nudgeShake} ${STORYBOARD_COMPOSE.CTA_SHAKE_MS}ms ${ANIM.EASE} both;
    `}

  ${REDUCED_MOTION} {
    animation: none;
  }
`;
